<script setup lang="ts">
import { Announcement } from "@mmh/types"
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue"
import { useRoute } from "vue-router"
import { ThemeImg } from "~/components/theme"
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "~/components/ui/dialog"

const { id, announcement } = defineProps<{
    id: string
    announcement: Announcement
}>()

const isOpen = ref(false)
const route = useRoute()

const getPopupState = () => {
    try {
        return JSON.parse(sessionStorage.getItem(`customPopupState-${id}`) || "{\"show\": true}")
    } catch {
        return { show: true }
    }
}
const savePopupState = (state: { show: boolean }) => {
    sessionStorage.setItem(`customPopupState-${id}`, JSON.stringify(state))
}

const isWithinDateRange = () => {
    const now = new Date()
    return new Date(announcement.startDate) <= now && now <= new Date(announcement.endDate)
}

const shouldShowPopup = computed(() => {
    const state = getPopupState()
    return announcement.routes.includes(route.path) && state.show && isWithinDateRange()
})

const closePopup = () => {
    isOpen.value = false
    savePopupState({ show: false })
}


watch(() => route.path, () => {
    if (shouldShowPopup.value) setTimeout(() => (isOpen.value = true), 100)
    else isOpen.value = false
})

const restoreInteractivity = () => {
    document.body.style.pointerEvents = ""
    document.body.style.overflow = ""
}

watch(isOpen, (open) => {
    // PATCH: If dialog closes (including when Open script closes it), restore page interactivity
    if (!open) {
        nextTick(() => {
            restoreInteractivity()
            // Also restore after a delay in case Open script sets it later
            setTimeout(restoreInteractivity, 200)
            setTimeout(restoreInteractivity, 500)
        })
    }
})

// Watch for body style changes (Open script may set pointer-events: none after dialog closes)
let bodyObserver: MutationObserver | null = null
onMounted(() => {
    bodyObserver = new MutationObserver(() => {
        if (document.body.style.pointerEvents === "none" && !isOpen.value) {
            restoreInteractivity()
        }
    })
    bodyObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ["style"]
    })
    if (shouldShowPopup.value) setTimeout(() => (isOpen.value = true), 100)
})

onUnmounted(() => {
    if (bodyObserver) {
        bodyObserver.disconnect()
    }
})
</script>

<template>
    <Dialog
        :open="isOpen"
        @update:open="(value) => !value && closePopup()"
    >
        <DialogContent class="md:max-w-[600px] lg:max-w-[800px] xl:max-w-[1000px] overflow-hidden border-8 p-0">
            <DialogTitle class="sr-only">
                {{ announcement.dialogTitle }}
            </DialogTitle>
            <DialogDescription class="sr-only">
                Popup
            </DialogDescription>
            <component
                :is="announcement.link ? 'a' : 'div'"
                :href="announcement.link"
                :target="announcement.link ? '_blank' : undefined"
                :rel="announcement.link ? 'noopener noreferrer' : undefined"
                :aria-label="announcement.link ? `${announcement.dialogTitle} (opens in a new tab)` : undefined"
                class="block w-full rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-4"
            >
                <ThemeImg
                    :src="announcement.imgUrl"
                    :alt="announcement.link ? '' : announcement.imgAltText"
                    class="hidden sm:block w-full min-h-0"
                />
                <ThemeImg
                    :src="announcement.mobileImgUrl"
                    :alt="announcement.link ? '' : announcement.imgAltText"
                    class="block sm:hidden w-full min-h-0"
                />
            </component>
        </DialogContent>
    </Dialog>
</template>
